import { useEffect, useMemo, useState } from 'react'
import {
  App as AntdApp,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  CheckCircleOutlined,
  DeleteOutlined,
  DollarOutlined,
  DownloadOutlined,
  FileTextOutlined,
  PlusOutlined,
  ShoppingCartOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import apiClient from '../../api/client'
import DataTable from '../../components/DataTable'
import PageHeader from '../../components/PageHeader'

// Orders backend: list/create-draft/place/cancel + nested /items + /actions.
// Order-Actions are the sequential queue Ubersmith-style; we display them
// read-only as a timeline. Re-run / skip is exposed via Provisioning-Jobs.

interface Order {
  id: number
  order_no?: string
  customer_id?: number
  customer_email?: string
  customer_name?: string
  status: 'draft' | 'placed' | 'cancelled'
  currency: string
  subtotal_net: string
  subtotal_tax: string
  total_gross: string
  created_at: string
  placed_at?: string
}

interface Product {
  id: number
  name: string
  kind?: string
  currency: string
  default_cycle?: string
  pricing: Record<string, string>
}

interface Customer {
  id: number
  customer_no: string
  company?: string
  email?: string
}

interface LineItem {
  key: number
  product_id: number
  product_name: string
  kind?: string
  domain?: string
  description: string
  billing_cycle: string
  unit_price: string
  quantity: number
  tax_code: string
}

const statusColors: Record<string, string> = {
  draft: 'default',
  placed: 'success',
  cancelled: 'red',
}

const statusLabels: Record<string, string> = {
  draft: 'Entwurf',
  placed: 'Aufgegeben',
  cancelled: 'Storniert',
}

const TAX_OPTIONS = [
  { value: 'std',    label: '19 % (std)' },
  { value: 'red',    label: '7 % (red)' },
  { value: 'zero',   label: '0 % (zero)' },
  { value: 'exempt', label: 'steuerfrei' },
]

const CYCLE_LABELS: Record<string, string> = {
  monthly: 'monatlich',
  quarterly: 'vierteljährl.',
  yearly: 'jährlich',
  biennial: '2-jährl.',
  triennial: '3-jährl.',
  quadrennial: '4-jährl.',
  quinquennial: '5-jährl.',
  sexennial: '6-jährl.',
  one_time: 'einmalig',
}

export default function Orders() {
  const { t } = useTranslation()
  const [statusFilter, setStatusFilter] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const preCustomerID = Number(searchParams.get('customer_id') || 0) || null

  useEffect(() => {
    if (searchParams.get('action') === 'create') setCreateOpen(true)
  }, [searchParams])

  const listQ = useQuery({
    queryKey: ['orders', statusFilter],
    queryFn: async () => {
      const params: Record<string, string> = {}
      if (statusFilter) params.status = statusFilter
      const res = await apiClient.get<{ data: { items: Order[] } | Order[] }>(
        '/api/v1/orders', { params },
      )
      const d = res.data.data as { items?: Order[] } | Order[]
      return Array.isArray(d) ? d : (d?.items ?? [])
    },
  })

  const fmtDate = (v?: string) =>
    v ? new Date(v).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: '2-digit',
      hour: '2-digit', minute: '2-digit',
    }) : '—'

  const customerCell = (r: Order) =>
    r.customer_id
      ? <Link to={`/customers/${r.customer_id}`} className="font-medium">
          {r.customer_name || r.customer_email || `#${r.customer_id}`}
        </Link>
      : <Typography.Text type="secondary">{r.customer_email || '—'}</Typography.Text>

  const columns: ColumnsType<Order> = [
    {
      title: t('order.no', 'Bestell-Nr.'),
      dataIndex: 'order_no',
      key: 'no',
      width: 150,
      render: (v: string | undefined, r: Order) => (
        <Link to={`/orders/${r.id}`}>
          <Typography.Text code>{v || `#${r.id}`}</Typography.Text>
        </Link>
      ),
    },
    {
      title: t('order.customer', 'Kunde'),
      key: 'customer',
      render: (_, r) => customerCell(r),
    },
    {
      title: t('order.status', 'Status'),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (v: string) => <Tag color={statusColors[v]}>{statusLabels[v] || v}</Tag>,
    },
    {
      title: t('order.total', 'Brutto'),
      key: 'total',
      align: 'right' as const,
      width: 140,
      render: (_, r) => (
        <Typography.Text strong>
          {parseFloat(r.total_gross).toFixed(2)} {r.currency}
        </Typography.Text>
      ),
    },
    {
      title: t('order.placed', 'Aufgegeben'),
      dataIndex: 'placed_at',
      key: 'placed',
      width: 150,
      render: (v?: string) => (
        <Typography.Text type="secondary" className="text-sm">{fmtDate(v)}</Typography.Text>
      ),
    },
  ]

  const renderMobileCard = (r: Order) => (
    <div className="mob-card">
      <div className="flex-between mb-6">
        <Link to={`/orders/${r.id}`}>
          <Typography.Text code className="font-semibold">{r.order_no || `#${r.id}`}</Typography.Text>
        </Link>
        <Tag color={statusColors[r.status]}>{statusLabels[r.status] || r.status}</Tag>
      </div>
      <div className="mb-4">{customerCell(r)}</div>
      <div className="flex-between">
        <Typography.Text type="secondary" className="text-sm">
          {fmtDate(r.placed_at || r.created_at)}
        </Typography.Text>
        <Typography.Text strong>
          {parseFloat(r.total_gross).toFixed(2)} {r.currency}
        </Typography.Text>
      </div>
    </div>
  )

  return (
    <>
      <PageHeader
        icon={<ShoppingCartOutlined />}
        title={t('order.title', 'Bestellungen')} subtitle={t('order.subtitle', 'Bestellprozess und Action-Queue verwalten.')}
        extra={
          <Space wrap>
            <Select
              allowClear
              placeholder={t('order.statusFilter', 'Status-Filter')}
              className="min-w-180"
              options={Object.entries(statusLabels).map(([value, label]) => ({ value, label }))}
              onChange={(v) => setStatusFilter(v ?? '')}
            />
            <Button icon={<DownloadOutlined />}
              href={`/api/v1/orders/export.csv${statusFilter ? '?status=' + statusFilter : ''}`}>
              {t('order.exportCsv', 'CSV-Export')}
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
              {t('order.new', 'Neue Bestellung')}
            </Button>
          </Space>
        }
      />
      {(() => {
        const rows = listQ.data || []
        const placed = rows.filter(r => r.status === 'placed')
        const drafts = rows.filter(r => r.status === 'draft').length
        const cancelled = rows.filter(r => r.status === 'cancelled').length
        const placedValue = placed.reduce((a, r) => a + parseFloat(r.total_gross || '0'), 0)
        return (
          <div className="kpi-grid kpi-grid-4 mb-16">
            <div className="kpi-tile">
              <span className="kpi-tile-label"><ShoppingCartOutlined /> Bestellungen</span>
              <span className="kpi-tile-value">{rows.length}</span>
              <span className="kpi-tile-sub">{statusFilter || 'Alle Status'}</span>
            </div>
            <div className={`kpi-tile ${placed.length > 0 ? 'kpi-success' : ''}`}>
              <span className="kpi-tile-label"><CheckCircleOutlined /> Platziert</span>
              <span className="kpi-tile-value">{placed.length}</span>
              <span className="kpi-tile-sub">{placedValue.toFixed(2)} € Umsatz</span>
            </div>
            <div className={`kpi-tile ${drafts > 0 ? 'kpi-info' : ''}`}>
              <span className="kpi-tile-label"><FileTextOutlined /> Entwürfe</span>
              <span className="kpi-tile-value">{drafts}</span>
              <span className="kpi-tile-sub">offen</span>
            </div>
            <div className={`kpi-tile ${cancelled > 0 ? 'kpi-warning' : ''}`}>
              <span className="kpi-tile-label"><DollarOutlined /> Storniert</span>
              <span className="kpi-tile-value">{cancelled}</span>
              <span className="kpi-tile-sub">{cancelled > 0 ? 'abgebrochen' : 'Keine'}</span>
            </div>
          </div>
        )
      })()}
      <Card className="content-card">
        <DataTable<Order>
          rowKey="id"
          columns={columns}
          dataSource={listQ.data || []}
          loading={listQ.isLoading}
          searchPlaceholder={t('order.searchPlaceholder', 'Suche Bestell-Nr. oder E-Mail')}
          renderMobileCard={renderMobileCard}
        />
      </Card>

      <CreateOrderModal
        open={createOpen}
        preCustomerID={preCustomerID}
        onClose={() => { setCreateOpen(false); }}
        onCreated={(id) => {
          setCreateOpen(false)
          listQ.refetch()
          navigate(`/orders/${id}`)
        }}
      />
    </>
  )
}

// ── CreateOrderModal ──────────────────────────────────────────────────────

let lineKey = 0

function CreateOrderModal({
  open,
  preCustomerID,
  onClose,
  onCreated,
}: {
  open: boolean
  preCustomerID: number | null
  onClose: () => void
  onCreated: (orderID: number) => void
}) {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const qc = useQueryClient()

  const [customerID, setCustomerID] = useState<number | null>(preCustomerID)
  const [currency, setCurrency] = useState('EUR')
  const [notes, setNotes] = useState('')
  const [lineItems, setLineItems] = useState<LineItem[]>([])

  // Add-item form state
  const [addProduct, setAddProduct] = useState<Product | null>(null)
  const [addCycle, setAddCycle] = useState('')
  const [addQty, setAddQty] = useState(1)
  const [addTaxCode, setAddTaxCode] = useState('std')

  useEffect(() => {
    if (open && preCustomerID) setCustomerID(preCustomerID)
  }, [open, preCustomerID])

  const customersQ = useQuery({
    queryKey: ['customers-for-order'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: Customer[] } | Customer[] }>(
        '/api/v1/customers?limit=500',
      )
      const d = res.data.data as { items?: Customer[] } | Customer[]
      return Array.isArray(d) ? d : (d?.items ?? [])
    },
    staleTime: 60_000,
  })

  const productsQ = useQuery({
    queryKey: ['products-for-order'],
    queryFn: async () => {
      const res = await apiClient.get<{ data: { items: Product[] } | Product[] }>('/api/v1/products?limit=200&active=true')
      const d = res.data.data as { items?: Product[] } | Product[]
      return Array.isArray(d) ? d : (d?.items ?? [])
    },
    staleTime: 60_000,
  })

  // Billing cycles available for the selected product
  const availableCycles = useMemo(() => {
    if (!addProduct?.pricing) return []
    return Object.keys(addProduct.pricing).filter(k => addProduct.pricing[k])
  }, [addProduct])

  // Auto-select default cycle when product changes
  useEffect(() => {
    if (addProduct) {
      const preferred = addProduct.default_cycle || availableCycles[0] || ''
      setAddCycle(preferred)
    }
  }, [addProduct]) // eslint-disable-line react-hooks/exhaustive-deps

  const addUnitPrice = addProduct && addCycle ? addProduct.pricing[addCycle] || '0.00' : '0.00'

  const handleAddLine = () => {
    if (!addProduct || !addCycle) {
      message.warning(t('order.selectProductFirst', 'Bitte Produkt und Abrechnungsintervall wählen'))
      return
    }
    setLineItems(prev => [...prev, {
      key: ++lineKey,
      product_id: addProduct.id,
      product_name: addProduct.name,
      kind: addProduct.kind,
      description: addProduct.name,
      billing_cycle: addCycle,
      unit_price: addUnitPrice,
      quantity: addQty,
      tax_code: addTaxCode,
    }])
    setAddProduct(null)
    setAddCycle('')
    setAddQty(1)
    setAddTaxCode('std')
  }

  const removeLine = (key: number) => setLineItems(prev => prev.filter(l => l.key !== key))
  const updateLineDomain = (key: number, domain: string) =>
    setLineItems(prev => prev.map(l => l.key === key ? { ...l, domain } : l))
  // Webhosting-Positionen brauchen zwingend eine Domain.
  const linesDomainOk = lineItems.every(l => l.kind !== 'hosting' || !!l.domain?.trim())

  const totalGross = lineItems.reduce((sum, l) => {
    const price = parseFloat(l.unit_price || '0')
    const qty = l.quantity || 1
    const taxPct = l.tax_code === 'std' ? 19 : l.tax_code === 'red' ? 7 : 0
    return sum + (price * qty * (1 + taxPct / 100))
  }, 0)

  const createAndPlace = useMutation({
    mutationFn: async () => {
      const body = {
        customer_id: customerID || undefined,
        currency,
        notes: notes || undefined,
        items: lineItems.map(l => ({
          product_id: l.product_id,
          description: l.description,
          quantity: l.quantity,
          unit_price: l.unit_price,
          tax_code: l.tax_code,
          billing_cycle: l.billing_cycle,
          config: l.domain?.trim() ? { domain: l.domain.trim().toLowerCase() } : undefined,
        })),
      }
      const draftRes = await apiClient.post<{ data: { id: number } }>('/api/v1/orders', body)
      const orderID = draftRes.data.data.id
      await apiClient.post(`/api/v1/orders/${orderID}/place`)
      return orderID
    },
    onSuccess: (id) => {
      message.success(t('order.placed', 'Bestellung platziert'))
      qc.invalidateQueries({ queryKey: ['orders'] })
      handleReset()
      onCreated(id)
    },
    onError: (e: unknown) => {
      const err = e as { response?: { data?: { error?: string } } }
      message.error(err.response?.data?.error || t('order.placeFailed', 'Bestellung fehlgeschlagen'))
    },
  })

  const handleReset = () => {
    setCustomerID(preCustomerID)
    setCurrency('EUR')
    setNotes('')
    setLineItems([])
    setAddProduct(null)
    setAddCycle('')
    setAddQty(1)
    setAddTaxCode('std')
  }

  const lineColumns: ColumnsType<LineItem> = [
    { title: t('order.product', 'Produkt'), key: 'prod', render: (_, r) => r.product_name },
    { title: t('order.cycle', 'Intervall'), key: 'cycle', width: 130,
      render: (_, r) => <Tag>{CYCLE_LABELS[r.billing_cycle] || r.billing_cycle}</Tag> },
    { title: t('invoice.qty', 'Menge'), dataIndex: 'quantity', key: 'qty', width: 70, align: 'right' as const },
    { title: t('invoice.unitPrice', 'Preis'), key: 'price', width: 100, align: 'right' as const,
      render: (_, r) => `${r.unit_price} ${currency}` },
    { title: t('invoice.tax', 'USt'), dataIndex: 'tax_code', key: 'tax', width: 90 },
    { title: t('order.domain', 'Domain'), key: 'domain', width: 190,
      render: (_, r) => r.kind === 'hosting'
        ? <Input size="small" placeholder="meinedomain.de"
            status={!r.domain?.trim() ? 'error' : undefined}
            value={r.domain || ''} onChange={(e) => updateLineDomain(r.key, e.target.value)} />
        : <Typography.Text type="secondary">—</Typography.Text> },
    { title: '', key: 'del', width: 50, align: 'center' as const,
      render: (_, r) => (
        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeLine(r.key)} />
      ) },
  ]

  return (
    <Modal
      open={open}
      onCancel={() => { handleReset(); onClose() }}
      title={<><ShoppingCartOutlined /> {t('order.newTitle', 'Neue Bestellung anlegen')}</>}
      footer={null}
      width={760}
      destroyOnClose
    >
      {/* Customer + Currency */}
      <Row gutter={12} className="mb-12">
        <Col xs={24} sm={16}>
          <Form.Item label={t('order.customer', 'Kunde')} className="mb-0">
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('order.selectCustomer', 'Kunde wählen')}
              value={customerID ?? undefined}
              onChange={(v: number) => setCustomerID(v)}
              options={(customersQ.data || []).map(c => ({
                value: c.id,
                label: `${c.customer_no} — ${c.company || c.email || '?'}`,
              }))}
              loading={customersQ.isLoading}
              className="w-full"
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={8}>
          <Form.Item label={t('invoice.currency', 'Währung')} className="mb-0">
            <Select
              value={currency}
              onChange={setCurrency}
              options={[
                { value: 'EUR', label: 'EUR' },
                { value: 'USD', label: 'USD' },
                { value: 'CHF', label: 'CHF' },
              ]}
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider className="my-12">{t('order.products', 'Produkte hinzufügen')}</Divider>

      {/* Add product row */}
      <Row gutter={8} className="mb-8" align="bottom">
        <Col xs={24} sm={10}>
          <Form.Item label={t('order.product', 'Produkt')} className="mb-0">
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('order.selectProduct', 'Produkt wählen')}
              value={addProduct?.id ?? undefined}
              onChange={(v: number) => {
                const p = (productsQ.data || []).find(x => x.id === v) ?? null
                setAddProduct(p)
              }}
              options={(productsQ.data || []).map(p => ({ value: p.id, label: p.name }))}
              loading={productsQ.isLoading}
            />
          </Form.Item>
        </Col>
        <Col xs={12} sm={6}>
          <Form.Item label={t('order.cycle', 'Intervall')} className="mb-0">
            <Select
              value={addCycle || undefined}
              placeholder="—"
              onChange={setAddCycle}
              disabled={!addProduct}
              options={availableCycles.map(c => ({
                value: c,
                label: `${CYCLE_LABELS[c] || c} — ${addProduct?.pricing[c] ?? ''} ${addProduct?.currency || ''}`,
              }))}
            />
          </Form.Item>
        </Col>
        <Col xs={6} sm={4}>
          <Form.Item label={t('invoice.qty', 'Menge')} className="mb-0">
            <InputNumber
              min={1} value={addQty} onChange={(v) => setAddQty(v ?? 1)}
              className="w-full"
            />
          </Form.Item>
        </Col>
        <Col xs={12} sm={4}>
          <Form.Item label={t('invoice.taxCode', 'USt')} className="mb-0">
            <Select value={addTaxCode} onChange={setAddTaxCode} options={TAX_OPTIONS} />
          </Form.Item>
        </Col>
      </Row>
      <Button icon={<PlusOutlined />} onClick={handleAddLine} disabled={!addProduct || !addCycle}
        className="mb-16">
        {t('order.addLine', 'Position hinzufügen')}
      </Button>

      {/* Line items table */}
      {lineItems.length > 0 && (
        <>
          <DataTable<LineItem>
            rowKey="key"
            size="small"
            dataSource={lineItems}
            columns={lineColumns}
            className="mb-8"
            renderMobileCard={(r: LineItem) => (
              <div className="mob-card">
                <div className="flex-between mb-6">
                  <strong>{r.product_name}</strong>
                  <Tag>{r.billing_cycle}</Tag>
                </div>
                <div className="flex-between text-sm" style={{ color: '#64748B' }}>
                  <span>{r.quantity} × {r.unit_price} €</span>
                </div>
              </div>
            )}
          />
          <div className="text-right mb-12">
            <Typography.Text strong>
              Σ Brutto: {totalGross.toFixed(2)} {currency}
            </Typography.Text>
          </div>
        </>
      )}

      {/* Notes */}
      <Form.Item label={t('common.notes', 'Notizen (intern)')}>
        <Input.TextArea
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('order.notesPlaceholder', 'z.B. „Telefonbestellung, Ref: Ticket #4711"')}
        />
      </Form.Item>

      {/* Footer */}
      <div className="flex justify-end gap-8 mt-8">
        <Button onClick={() => { handleReset(); onClose() }}>
          {t('common.cancel', 'Abbrechen')}
        </Button>
        <Button
          type="primary"
          icon={<ShoppingCartOutlined />}
          disabled={!customerID || lineItems.length === 0 || !linesDomainOk}
          loading={createAndPlace.isPending}
          onClick={() => createAndPlace.mutate()}
        >
          {t('order.placeBtn', 'Bestellung platzieren')}
        </Button>
      </div>
    </Modal>
  )
}
